/**
 * Preview Modal for Automation Rules
 * Shows a dry-run preview of what events would be affected by the rule
 */

import { useState } from 'react'
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ArrowLeft, ChevronDown, Check, X, Clock, RefreshCw, CalendarSyncIcon } from '@/components/icons'
import { Loader } from '@/components/ai-elements/loader'
import { cn } from '@/lib/utils'
import type { PreviewData, GoogleCalendarColorId } from './types'
import { GOOGLE_CALENDAR_COLORS } from './types'

interface PreviewModalProps {
  open: boolean
  onClose: () => void
  loading: boolean
  data: PreviewData | null
  error: string | null
  onRetry: () => void
  /** If true, shows "Create Rule" button instead of "Done" */
  isCreateMode?: boolean
  /** Called when "Create Rule" is clicked in create mode */
  onCreateRule?: () => void
  /** True when rule creation is in progress */
  creating?: boolean
}

export function PreviewModal({ open, onClose, loading, data, error, onRetry, isCreateMode, onCreateRule, creating }: PreviewModalProps) {
  const [toUpdateExpanded, setToUpdateExpanded] = useState(true)
  const [skippedExpanded, setSkippedExpanded] = useState(false)

  // Check if calendar is not synced yet
  const isCalendarNotSynced = data?.message?.toLowerCase().includes('calendar not synced') || 
                              data?.message?.toLowerCase().includes('not synced yet') ||
                              data?.message?.toLowerCase().includes('preview will be available after initial sync')

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        className="
          max-w-none
          p-0
          border-0 shadow-none
          w-full h-[90vh] max-h-[90vh]
          md:w-[600px] md:h-auto md:max-h-[80vh]
          flex flex-col
          fixed !top-auto inset-x-0 !bottom-0 !left-0 !translate-x-0 !translate-y-0
          md:!left-1/2 md:!top-1/2 md:!bottom-auto md:inset-x-auto md:!-translate-x-1/2 md:!-translate-y-1/2
          rounded-t-3xl md:rounded-lg
        "
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        hideClose
      >
        <DialogTitle className="sr-only">Rule Preview</DialogTitle>
        <DialogDescription className="sr-only">
          Preview of which events would be affected by this automation rule
        </DialogDescription>

        {/* Header - Hide when calendar not synced */}
        {!isCalendarNotSynced && (
          <DialogHeader className="px-2 pt-6 pb-4 flex-shrink-0">
            <div className="flex items-center justify-between gap-4">
              <Button
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  onClose()
                }}
                className="flex items-center gap-2 md:px-4 px-2 md:rounded-md rounded-full"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden md:inline">Back</span>
              </Button>

              <h2 className="flex-1 text-center font-semibold text-lg">
                Rule Preview
              </h2>

              <div className="w-[68px] md:w-[76px]"></div>
            </div>
          </DialogHeader>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-6 pb-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 space-y-3">
              <Loader size={24} className="text-muted-foreground" />
              <p className="text-muted-foreground">Analyzing events...</p>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <X className="h-8 w-8 text-destructive" />
              </div>
              <p className="text-sm text-destructive text-center max-w-sm">{error}</p>
              <Button variant="outline" onClick={onRetry} className="mt-2">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* Calendar not synced - simplified view */}
              {isCalendarNotSynced ? (
                <div className="flex flex-col items-center justify-center py-12 space-y-4 min-h-[200px]">
                  <CalendarSyncIcon className="h-16 w-16 text-muted-foreground mb-4" />
                  <div className="text-center max-w-sm space-y-1">
                    <p className="text-muted-foreground">
                      Calendar not synced yet.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Preview will be available after initial sync.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Message (e.g., calendar not synced yet) */}
                  {data.message && (
                    <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                      {data.message}
                    </div>
                  )}

                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-3">
                    <StatCard
                      label="Total Events"
                      value={data.total_events}
                      icon={<Clock className="h-4 w-4" />}
                    />
                    <StatCard
                      label="Will Update"
                      value={data.events_to_update}
                      icon={<Check className="h-4 w-4" />}
                      variant="success"
                    />
                    <StatCard
                      label="Skipped"
                      value={data.events_skipped}
                      icon={<X className="h-4 w-4" />}
                      variant="muted"
                    />
                  </div>

                  {/* Recurrence Breakdown */}
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold mb-3">By Event Type</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="h-3.5 w-3.5" />
                            <span>Regular Events</span>
                          </div>
                          <div className="pl-5 text-sm">
                            <span className="text-primary font-medium">{data.by_recurrence.regular.to_update}</span>
                            <span className="text-muted-foreground"> to update, </span>
                            <span className="text-muted-foreground">{data.by_recurrence.regular.skipped}</span>
                            <span className="text-muted-foreground"> skipped</span>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <RefreshCw className="h-3.5 w-3.5" />
                            <span>Recurring Events</span>
                          </div>
                          <div className="pl-5 text-sm">
                            <span className="text-primary font-medium">{data.by_recurrence.recurring.to_update}</span>
                            <span className="text-muted-foreground"> to update, </span>
                            <span className="text-muted-foreground">{data.by_recurrence.recurring.skipped}</span>
                            <span className="text-muted-foreground"> skipped</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Events to Update List */}
                  {data.events_to_update_list.length > 0 && (
                    <Collapsible open={toUpdateExpanded} onOpenChange={setToUpdateExpanded}>
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          className="w-full justify-between h-auto py-3 px-4 hover:bg-accent"
                        >
                          <span className="font-semibold text-sm">
                            Events to Update ({data.events_to_update_list.length})
                          </span>
                          <ChevronDown className={cn(
                            "h-4 w-4 transition-transform",
                            toUpdateExpanded && "rotate-180"
                          )} />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <Card className="mt-2">
                          <CardContent className="p-0 divide-y divide-border">
                            {data.events_to_update_list.slice(0, 50).map((event) => (
                              <EventItem
                                key={event.event_id}
                                event={event}
                                type="update"
                              />
                            ))}
                            {data.events_to_update_list.length > 50 && (
                              <div className="p-3 text-center text-sm text-muted-foreground">
                                ... and {data.events_to_update_list.length - 50} more events
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {/* Skipped Events List */}
                  {data.events_skipped_list.length > 0 && (
                    <Collapsible open={skippedExpanded} onOpenChange={setSkippedExpanded}>
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          className="w-full justify-between h-auto py-3 px-4 hover:bg-accent"
                        >
                          <span className="font-semibold text-sm text-muted-foreground">
                            Skipped Events ({data.events_skipped_list.length})
                          </span>
                          <ChevronDown className={cn(
                            "h-4 w-4 transition-transform text-muted-foreground",
                            skippedExpanded && "rotate-180"
                          )} />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <Card className="mt-2">
                          <CardContent className="p-0 divide-y divide-border">
                            {data.events_skipped_list.slice(0, 50).map((event) => (
                              <EventItem
                                key={event.event_id}
                                event={event}
                                type="skipped"
                              />
                            ))}
                            {data.events_skipped_list.length > 50 && (
                              <div className="p-3 text-center text-sm text-muted-foreground">
                                ... and {data.events_skipped_list.length - 50} more events
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {/* Empty state */}
                  {data.total_events === 0 && !data.message && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No future events found in this calendar.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 flex-shrink-0">
          {isCalendarNotSynced ? (
            // When calendar not synced, only show Create Rule button (if in create mode)
            isCreateMode && onCreateRule ? (
              <Button 
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  onCreateRule()
                }} 
                disabled={loading || creating || !!error}
                className="w-full" 
                size="lg"
              >
                {creating ? (
                  <>
                    <Loader size={16} className="mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Rule'
                )}
              </Button>
            ) : null
          ) : (
            // Normal footer when calendar is synced
            <>
              {isCreateMode && onCreateRule ? (
                <Button 
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onCreateRule()
                  }} 
                  disabled={loading || creating || !!error}
                  className="w-full" 
                  size="lg"
                >
                  {creating ? (
                    <>
                      <Loader size={16} className="mr-2" />
                      Creating...
                    </>
                  ) : (
                    'Create Rule'
                  )}
                </Button>
              ) : (
                <Button 
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onClose()
                  }} 
                  className="w-full" 
                  size="lg"
                >
                  Done
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// Stat Card Component
interface StatCardProps {
  label: string
  value: number
  icon: React.ReactNode
  variant?: 'default' | 'success' | 'muted'
}

function StatCard({ label, value, icon, variant = 'default' }: StatCardProps) {
  return (
    <Card>
      <CardContent className="p-3 text-center">
        <div className={cn(
          "flex items-center justify-center gap-1.5 mb-1",
          variant === 'success' && "text-primary",
          variant === 'muted' && "text-muted-foreground"
        )}>
          {icon}
          <span className="text-2xl font-bold">{value}</span>
        </div>
        <p className="text-xs text-muted-foreground">{label}</p>
      </CardContent>
    </Card>
  )
}

// Event Item Component
interface EventItemProps {
  event: {
    event_id: string
    event_title: string | null
    event_start_time: string | null
    event_is_recurring: boolean
    current_color: string | null
    target_color: string | null
    matching_rule_name: string | null
    skip_reason?: string
    applied_rule_priority?: number
    added_attendees?: string[]
    visibility_updated?: boolean
  }
  type: 'update' | 'skipped'
}

function EventItem({ event, type }: EventItemProps) {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown date'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Invalid date'
    }
  }

  const getColorHex = (colorId: string | null) => {
    if (!colorId) return null
    return GOOGLE_CALENDAR_COLORS[colorId as GoogleCalendarColorId]?.hex || null
  }

  const currentColorHex = getColorHex(event.current_color)
  const targetColorHex = getColorHex(event.target_color)

  return (
    <div className="p-3 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <p className="text-sm font-medium truncate">
            {event.event_title || 'Untitled Event'}
          </p>
          {event.event_is_recurring && (
            <RefreshCw className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {formatDate(event.event_start_time)}
        </p>
        {type === 'skipped' && event.skip_reason && (
          <p className="text-xs text-muted-foreground mt-1 italic">
            {event.skip_reason}
          </p>
        )}
      </div>
      
      {type === 'update' && event.target_color !== undefined && event.target_color !== null && (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {currentColorHex ? (
            <div
              className="w-4 h-4 rounded-full border border-border"
              style={{ backgroundColor: currentColorHex }}
              title={`Current: ${GOOGLE_CALENDAR_COLORS[event.current_color as GoogleCalendarColorId]?.name || 'Unknown'}`}
            />
          ) : (
            <div className="w-4 h-4 rounded-full border border-dashed border-border bg-background" title="No color" />
          )}
          <span className="text-xs text-muted-foreground">→</span>
          {targetColorHex ? (
            <div
              className="w-4 h-4 rounded-full border border-border"
              style={{ backgroundColor: targetColorHex }}
              title={`New: ${GOOGLE_CALENDAR_COLORS[event.target_color as GoogleCalendarColorId]?.name || 'Unknown'}`}
            />
          ) : (
            <div className="w-4 h-4 rounded-full border border-dashed border-border bg-background" title="Default" />
          )}
        </div>
      )}
      
      {type === 'update' && event.added_attendees && event.added_attendees.length > 0 && (
        <div className="flex-shrink-0 text-xs text-muted-foreground text-right max-w-[200px]">
          <span className="font-medium">+{event.added_attendees.length} attendee{event.added_attendees.length > 1 ? 's' : ''}</span>
          {event.visibility_updated && (
            <span className="block text-[10px]">Set to private</span>
          )}
        </div>
      )}
      
      {/* Show applied rule color for skipped events (when matched by higher priority rule) */}
      {type === 'skipped' && targetColorHex && (
        <div 
          className="flex items-center gap-1.5 flex-shrink-0"
          title={`Applied by: ${event.matching_rule_name || 'Unknown rule'} (priority ${event.applied_rule_priority ?? '?'})`}
        >
          <div
            className="w-4 h-4 rounded-full border border-border"
            style={{ backgroundColor: targetColorHex }}
          />
        </div>
      )}
    </div>
  )
}

